import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trophy, Clock, ExternalLink, XCircle } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { displayCompanyName } from "@/lib/displayCompanyName";

interface Props {
  load: any;
  bids: any[];
}

export function BidsTab({ load, bids }: Props) {
  const navigate = useNavigate();
  const isAwarded = load?.status === "awarded" || !!load?.awarded_bid_id;
  // Retracted bids stay visible for transparency, but never count as active.
  const activeCount = bids.filter((b) => b.status !== "withdrawn").length;
  const retractedCount = bids.length - activeCount;
  const sorted = [...bids].sort((a, b) => {
    if (a.id === load?.awarded_bid_id) return -1;
    if (b.id === load?.awarded_bid_id) return 1;
    return a.bid_amount - b.bid_amount;
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {isAwarded ? "Award Summary" : "Bids Received"} · {activeCount} bid{activeCount === 1 ? "" : "s"}
          {retractedCount > 0 && ` · ${retractedCount} retracted`}
        </p>
        {!isAwarded && (
          <Button
            size="sm"
            className="h-7 gap-1.5 text-xs bg-brand-teal hover:bg-brand-teal/90 text-white"
            onClick={() => navigate(`/lr-direct/live/${load.id}`)}
          >
            <ExternalLink className="h-3 w-3" /> Open Bid Room
          </Button>
        )}
      </div>

      {sorted.length === 0 ? (
        <Card className="p-6 text-center">
          <p className="text-sm text-muted-foreground">No bids received yet.</p>
        </Card>
      ) : (
        <div className="grid gap-2">
          {sorted.map((bid) => {
            const isWinner = bid.id === load?.awarded_bid_id || bid.status === "accepted";
            const isRejected = bid.status === "rejected";
            const isRetracted = bid.status === "withdrawn";
            return (
              <Card
                key={bid.id}
                className={`p-3 transition-all ${
                  isWinner
                    ? "border-brand-teal bg-brand-teal/5"
                    : isRetracted
                      ? "opacity-60 bg-muted/40 border-dashed"
                      : isRejected
                        ? "opacity-60"
                        : ""
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-semibold">{displayCompanyName(bid.broker_company_name) || "Broker"}</span>
                  <div className="flex items-center gap-2">
                    {isWinner && (
                      <Badge className="bg-brand-teal/10 text-brand-teal text-[10px] uppercase font-bold border-0 gap-1">
                        <Trophy className="h-3 w-3" /> Winner
                      </Badge>
                    )}
                    {isRetracted && (
                      <Badge variant="outline" className="gap-1 border-dashed text-[10px] uppercase text-muted-foreground">
                        <XCircle className="h-3 w-3" /> Retracted
                      </Badge>
                    )}
                    {isRejected && <Badge variant="outline" className="text-[10px] uppercase">Not Selected</Badge>}
                    {!isWinner && !isRejected && bid.status === "pending" && (
                      <Badge className="bg-emerald-500/15 text-emerald-500 text-[10px] uppercase font-bold border-0">Pending</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
                      {bid.rate_type === "per_mile" ? "Per Mile" : "Flat Rate"}
                    </p>
                    <p className={`text-xl font-bold tabular-nums ${isRetracted ? "line-through text-muted-foreground" : ""}`}>
                      ${Number(bid.bid_amount).toLocaleString()}
                    </p>
                    {load?.total_miles && (
                      <p className="text-[10px] text-muted-foreground tabular-nums">${(bid.bid_amount / load.total_miles).toFixed(2)}/mi</p>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {format(new Date(bid.submitted_at), "MMM d, h:mm a")}
                  </p>
                </div>
                {bid.message && <p className="text-xs text-muted-foreground italic mt-1.5">"{bid.message}"</p>}
                {isRetracted && bid.withdraw_reason && (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    <span className="font-semibold uppercase text-[10px] tracking-wider">Retraction reason: </span>
                    {bid.withdraw_reason}
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
